import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useVoucherTypes } from '../hooks/useVoucherTypes'
import { useCompanies } from '../hooks/useCompanies'
import { AddVoucherTypeModal } from '../components/AddVoucherTypeModal'
import { AddCompanyModal } from '../components/AddCompanyModal'
import '../styles/vouchers.scss'
import { FaPlus, FaTrash, FaBuilding } from 'react-icons/fa'

export const Vouchers = () => {
    const user = useSelector(state => state.userController.user)
    const navigate = useNavigate()
    const { getVoucherTypes, deleteVoucherType, loading: voucherTypesLoading } = useVoucherTypes()
    const { getCompanies, loading: companiesLoading } = useCompanies()
    const [voucherTypes, setVoucherTypes] = useState([])
    const [isTypeModalOpen, setIsTypeModalOpen] = useState(false)
    const [isCompanyModalOpen, setIsCompanyModalOpen] = useState(false)
    const [deleteLoading, setDeleteLoading] = useState('')
    const [companies, setCompanies] = useState([])
    const [selectedCompanyId, setSelectedCompanyId] = useState('')

    const selectedCompany = useMemo(
        () => companies.find(c => c._id === selectedCompanyId) || null,
        [companies, selectedCompanyId]
    )

    useEffect(() => {
        if (!user) {
            navigate('/login')
            return
        }
        fetchCompanies()
    }, [user])

    const fetchCompanies = async () => {
        const list = await getCompanies()
        setCompanies(list || [])
        if (list?.length) {
            setSelectedCompanyId((prev) => {
                const stillExists = list.find(c => c._id === prev)
                return stillExists ? prev : list[0]._id
            })
        } else {
            setSelectedCompanyId('')
            setVoucherTypes([])
        }
    }

    useEffect(() => {
        if (selectedCompany) {
            fetchVoucherTypes(selectedCompany._id)
        }
    }, [selectedCompany])

    const fetchVoucherTypes = async (companyId) => {
        if (!companyId) {
            setVoucherTypes([])
            return
        }
        const types = await getVoucherTypes(companyId)
        setVoucherTypes(types || [])
    }

    const handleTypeClick = (type) => {
        navigate(`/vouchers/${type._id}/versions?companyId=${selectedCompany?._id || ''}`)
    }

    const handleOpenModal = () => {
        if (!selectedCompany) return
        setIsTypeModalOpen(true)
    }

    const handleCloseModal = () => {
        setIsTypeModalOpen(false)
        // Refresh the list after adding new type
        fetchVoucherTypes(selectedCompany?._id)
    }

    const handleCompanyCreated = (company) => {
        fetchCompanies()
        if (company?._id) {
            setSelectedCompanyId(company._id)
        }
    }

    const handleCompanyClick = (companyId) => {
        setSelectedCompanyId(companyId)
    }

    const handleDelete = async (e, typeId) => {
        e.stopPropagation() // Prevent card click
        setDeleteLoading(typeId)
        try {
            await deleteVoucherType(typeId)
            // Refresh list after delete
            fetchVoucherTypes()
        } catch (error) {
            console.error('Error deleting voucher type:', error)
        } finally {
            setDeleteLoading('')
        }
    }

    const isLoading = voucherTypesLoading || companiesLoading

    if (isLoading) {
        return (
            <section className='container vouchers'>
                <div className="loading">جاري التحميل...</div>
            </section>
        )
    }

    return (
        <section className='container vouchers'>
            <div className="header">
                <div className="title">
                    <h1 className="TXT-heading color-normal">الكروت</h1>
                    <p className="TXT-normal color-light">إدارة الكروت</p>
                </div>
                <div className="actions">
                    <button 
                        className="secondary-btn"
                        onClick={() => setIsCompanyModalOpen(true)}
                    >
                        <FaBuilding />
                        <span>إضافة شركة</span>
                    </button>
                    <button 
                        className="create-btn"
                        onClick={handleOpenModal}
                        disabled={!selectedCompany}
                        title={selectedCompany ? 'إضافة فئة جديدة' : 'اختر شركة أولاً'}
                    >
                        <FaPlus />
                        <span>إضافة فئة جديدة</span>
                    </button>
                </div>
            </div>

            <div className="cards-container">
                <div className="companies">
                    <h3 className="section-title">الشركات</h3>
                    <div className="companies-grid">
                        {companies.map((company) => (
                            <div
                                key={company._id}
                                className={`company-card ${selectedCompanyId === company._id ? 'active' : ''}`}
                                onClick={() => handleCompanyClick(company._id)}
                            >
                                <div className="company-header">
                                    <span className="company-number">#{company.number}</span>
                                    {company.logoUrl ? (
                                        <img src={company.logoUrl} alt={company.name} className="company-logo" />
                                    ) : (
                                        <div className="logo-placeholder">{company.name.charAt(0)}</div>
                                    )}
                                </div>
                                <div className="company-name">{company.name}</div>
                            </div>
                        ))}
                        {!companies.length && (
                            <div className="empty">لا توجد شركات بعد. أضف شركة للبدء.</div>
                        )}
                    </div>
                </div>

                <div className="voucher-types">
                    <div className="section-header">
                        <h3 className="section-title">فئات الكروت</h3>
                        {selectedCompany && <span className="company-chip">{selectedCompany.name}</span>}
                    </div>
                    {selectedCompany ? (
                        <div className="voucher-grid">
                            {voucherTypes.map(type => (
                                <div 
                                    key={type._id} 
                                    className="voucher-card"
                                    onClick={() => handleTypeClick(type)}
                                >
                                    <div className="delete-btn" onClick={(e) => handleDelete(e, type._id)}>
                                        {deleteLoading === type._id ? (
                                            <span className="loading-spinner"></span>
                                        ) : (
                                            <FaTrash />
                                        )}
                                    </div>
                                    <div className="value">
                                        <h2>
                                            <span>{type.voucherValue}</span>
                                            دينار
                                        </h2>
                                    </div>
                                    <div className="available-count">
                                        <p>الكروت المتاحة:</p>
                                        <span className="count">{type.availableCount}</span>
                                    </div>
                                </div>
                            ))}
                            {!voucherTypes.length && (
                                <div className="empty">لا توجد فئات لهذه الشركة بعد</div>
                            )}
                        </div>
                    ) : (
                        <div className="empty">اختر شركة لعرض فئات الكروت</div>
                    )}
                </div>
            </div>

            <AddVoucherTypeModal 
                isOpen={isTypeModalOpen}
                onClose={handleCloseModal}
                companyId={selectedCompany?._id}
            />

            <AddCompanyModal
                isOpen={isCompanyModalOpen}
                onClose={() => setIsCompanyModalOpen(false)}
                onCreated={handleCompanyCreated}
            />
        </section>
    )
}

export default Vouchers